import React, { type ComponentProps } from "react"

import { type PermissionProjectPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import {
  type Project,
  type ProjectUpdateData
} from "../../types/project.types.ts"
import { ProjectCustomerInformationCard } from "./ProjectCustomerInformationCard.tsx"
import { ProjectFilesCard } from "./ProjectFilesCard.tsx"
import { ProjectFinancialSummaryCard } from "./ProjectFinancialSummaryCard.tsx"
import { ProjectInformationCard } from "./ProjectInformationCard.tsx"
import { ProjectLocationCard } from "./ProjectLocationCard.tsx"
import { ProjectNotesCard } from "./ProjectNotesCard.tsx"

interface ProjectOverviewCardsProps {
  project: Project
  filterOptions: Record<string, any>
  projectPermissions: PermissionProjectPermissions | undefined
  canEdit: boolean
  /** The tab is open to anyone who may view the project, so it hides what the
   *  Finance and Files tabs would. The Edit Details page shows both. */
  showFinancialSummary?: boolean
  showFiles?: boolean
  onSave: (patch: Partial<ProjectUpdateData>) => Promise<boolean>
  addProjectType: InformationCardProps["addProjectType"]
  removeProjectType: InformationCardProps["removeProjectType"]
}

type InformationCardProps = ComponentProps<typeof ProjectInformationCard>

/**
 * The project's six profile cards. Shared by the dedicated Edit Details page
 * (reached from the Projects list) and the project's own Overview tab, so the
 * two can never drift apart — an edit made in either is the same card.
 */
export function ProjectOverviewCards({
  project,
  filterOptions,
  projectPermissions,
  canEdit,
  showFinancialSummary = true,
  showFiles = true,
  onSave,
  addProjectType,
  removeProjectType
}: ProjectOverviewCardsProps) {
  return (
    /*
      One shared 2-column grid for all six cards, rather than three
      independent Flex rows — a Flex row only agrees on width within
      itself, so nothing guaranteed Project Information's left edge
      lined up with Financial Snapshot's two rows down. A grid's
      column tracks are shared by every row that uses them, so all
      three rows' left/right edges are structurally the same line,
      not just visually close.
    */
    <div
      className={"grid gap-4"}
      style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)" }}
    >
      <ProjectInformationCard
        project={project}
        filterOptions={filterOptions}
        canEdit={canEdit}
        onSave={onSave}
        addProjectType={addProjectType}
        removeProjectType={removeProjectType}
      />
      <ProjectCustomerInformationCard
        project={project}
        filterOptions={filterOptions}
        canEdit={canEdit}
        onSave={onSave}
      />

      {showFinancialSummary && (
        <ProjectFinancialSummaryCard
          project={project}
          projectPermissions={projectPermissions}
          canEdit={canEdit}
          onSave={onSave}
        />
      )}
      <ProjectLocationCard
        project={project}
        filterOptions={filterOptions}
        canEdit={canEdit}
        onSave={onSave}
      />

      {showFiles && (
        <ProjectFilesCard project={project} canEdit={canEdit} onSave={onSave} />
      )}
      <ProjectNotesCard project={project} canEdit={canEdit} onSave={onSave} />
    </div>
  )
}
